import React, { useEffect, useState } from "react";
import "./index.css";

export default function Features({
    data = {},
    isEditing,
    onChange
}) {

    // ✅ RECEIVE FROM PARENT
    const {
        features = [],
        identifiers = []
    } = data;

    // ✅ LOCAL STATE
    const [nodes, setNodes] = useState(identifiers);
    const [featureList, setFeatureList] = useState(features);

    // ✅ SYNC FROM PARENT
    useEffect(() => {
        setNodes(identifiers || []);
    }, [identifiers]);

    useEffect(() => {
        setFeatureList(features || []);
    }, [features]);

    // ---------------- GRAPH STATE ----------------
    const [activeIndex, setActiveIndex] = useState(0);
    const [hoverIndex, setHoverIndex] = useState(null);

    useEffect(() => {

        if (isEditing) return;

        if (!nodes.length) return;

        const interval = setInterval(() => {

            setActiveIndex((prev) =>
                (prev + 1) % nodes.length
            );

        }, 2000);

        return () => clearInterval(interval);

    }, [nodes.length, isEditing]);

    const currentIndex =
        hoverIndex !== null
            ? hoverIndex
            : activeIndex;

    // =========================
    // ✅ UPDATE HELPERS
    // =========================

    const pushUpdate = (
        updatedFeatures,
        updatedNodes
    ) => {

        if (!onChange) return;

        onChange({
            features: updatedFeatures,
            identifiers: updatedNodes
        });
    };

    // =========================
    // ✅ NODE HANDLERS
    // =========================

    const updateNode = (i, val) => {

        const copy = [...nodes];

        copy[i] = val;

        setNodes(copy);

        pushUpdate(featureList, copy);
    };

    const addNode = () => {

        const updated = [
            ...nodes,
            "New"
        ];

        setNodes(updated);

        pushUpdate(featureList, updated);
    };

    const removeNode = (i) => {

        const updated =
            nodes.filter((_, idx) => idx !== i);

        setNodes(updated);

        pushUpdate(featureList, updated);
    };

    // =========================
    // ✅ FEATURE HANDLERS
    // =========================

    const updateFeature = (i, val) => {

        const copy = [...featureList];

        copy[i] = val;

        setFeatureList(copy);

        pushUpdate(copy, nodes);
    };

    const addFeature = () => {

        const updated = [
            ...featureList,
            "New feature"
        ];

        setFeatureList(updated);

        pushUpdate(updated, nodes);
    };

    const removeFeature = (i) => {

        const updated =
            featureList.filter(
                (_, idx) => idx !== i
            );

        setFeatureList(updated);

        pushUpdate(updated, nodes);
    };

    // =========================================================
    // 🟣 EDIT MODE
    // =========================================================
    if (isEditing) {

        return (
            <div className="glxwrapper-simple">

                {/* FEATURES */}
                <div className="glxblock">

                    <h3 className="glxheading">
                        Features
                    </h3>

                    {featureList.map((f, i) => (

                        <div
                            key={i}
                            className="editRow"
                        >

                            <input
                                value={f}
                                onChange={(e) =>
                                    updateFeature(
                                        i,
                                        e.target.value
                                    )
                                }
                            />

                            <button
                                type="button"
                                onClick={() =>
                                    removeFeature(i)
                                }
                            >
                                ✕
                            </button>

                        </div>
                    ))}

                    <button
                        type="button"
                        className="addBtn"
                        onClick={addFeature}
                    >
                        + Add Feature
                    </button>

                </div>

                {/* IDENTIFIERS */}
                <div className="glxblock">

                    <h3 className="glxheading">
                        Identifiers
                    </h3>

                    {nodes.map((n, i) => (

                        <div
                            key={i}
                            className="editRow"
                        >

                            <input
                                value={n}
                                onChange={(e) =>
                                    updateNode(
                                        i,
                                        e.target.value
                                    )
                                }
                            />

                            <button
                                type="button"
                                onClick={() =>
                                    removeNode(i)
                                }
                            >
                                ✕
                            </button>

                        </div>
                    ))}

                    <button
                        type="button"
                        className="addBtn"
                        onClick={addNode}
                    >
                        + Add Node
                    </button>

                </div>
            </div>
        );
    }

    // =========================================================
    // 🔵 VIEW MODE
    // =========================================================
    return (
        <div className="glxwrapper">

            {/* LEFT TEXT BLOCK */}
            <div className="glxpoints">

                <h3 className="glxheading">
                    Features
                </h3>

                <div className="glxlist">

                    {featureList.map((f, i) => (

                        <div
                            key={i}
                            className="listitem"
                        >
                            {f}
                        </div>

                    ))}

                </div>
            </div>

            {/* GRAPH */}
            <div className="graphholder">

                <div className="glxcontainer">

                    <div className="glxcenternode">
                        GLUE ID
                    </div>

                    {nodes.map((node, index) => {

                        const angle =
                            (index / nodes.length)
                            * 2
                            * Math.PI;

                        const radius = 150;

                        const x =
                            Math.cos(angle)
                            * radius;

                        const y =
                            Math.sin(angle)
                            * radius;

                        const rotation =
                            (angle * 180)
                            / Math.PI;

                        const isActive =
                            index === currentIndex;

                        return (
                            <div key={node + index}>

                                {/* LINE */}
                                <div
                                    className={`glxarrow ${
                                        isActive
                                            ? "active"
                                            : ""
                                    }`}
                                    style={{
                                        left: "50%",
                                        top: "50%",
                                        height: `${radius}px`,
                                        transformOrigin:
                                            "top center",
                                        transform:
                                            `rotate(${rotation - 90}deg)`
                                    }}
                                />

                                {/* NODE */}
                                <div
                                    className={`glxnode ${
                                        isActive
                                            ? "active"
                                            : ""
                                    }`}
                                    style={{
                                        fontSize:
                                            node === "..."
                                                ? 37
                                                : 11,

                                        left: "50%",
                                        top: "50%",

                                        "--x": `${x}px`,
                                        "--y": `${y}px`,
                                    }}
                                    onMouseEnter={() =>
                                        setHoverIndex(index)
                                    }
                                    onMouseLeave={() =>
                                        setHoverIndex(null)
                                    }
                                >
                                    {node}
                                </div>

                            </div>
                        );
                    })}

                </div>
            </div>

        </div>
    );
}